import React, { useState, useEffect } from 'react';
import { User, Phone, UserCheck, CreditCard, Edit, X, Save, Plus, Minus } from 'lucide-react';
import { patientService } from '../../../patient/services/patientService';

const GeneralDetailsContent = ({ patientId }) => {
  const [patient, setPatient] = useState(null);
  const [editablePatient, setEditablePatient] = useState({});
  const [editableAccountsDetails, setEditableAccountsDetails] = useState({});
  const [patientAccountsDetails, setPatientAccountsDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [error, setError] = useState(null);

  // Expansion states
  const [isGeneralDetailsExpanded, setIsGeneralDetailsExpanded] = useState(true);
  const [isContactDetailsExpanded, setIsContactDetailsExpanded] = useState(true);
  const [isNextOfKinDetailsExpanded, setIsNextOfKinDetailsExpanded] = useState(true);
  const [isOtherDetailsExpanded, setIsOtherDetailsExpanded] = useState(true);

  // Load patient data (try real data first, fallback to mock)
  useEffect(() => {
    console.log('GeneralDetailsContent loading for patient:', patientId);
    const loadPatientData = async () => {
      try {
        setLoading(true);
        console.log('🔍 Fetching real patient data for ID:', patientId);
        
        // Try to fetch real patient data using patient_id (e.g., "P000002")
        const patientData = await patientService.getPatientByPatientId(patientId);
        const accountsData = await patientService.getPatientAccountsDetailsByPatientId(patientData.id);
        
        console.log('✅ Real patient data loaded:', patientData);
        console.log('✅ Real accounts data loaded:', accountsData);
        
        setPatient(patientData);
        setEditablePatient({ ...patientData });
        setPatientAccountsDetails(accountsData);
        setEditableAccountsDetails({ ...accountsData });
        setLoading(false);
        
      } catch (error) {
        console.error('❌ Failed to load real data, using mock data:', error);
        loadMockPatientData();
      }
    };
    
    const loadMockPatientData = () => {
      const mockPatientData = {
        patient_id: patientId || 'P001',
        first_name: 'John',
        last_name: 'Smith',
        dob: '1985-06-15',
        gender: 'Male',
        phone: '+1 (555) 123-4567',
        user_email: 'john.smith@email.com',
        address: '123 Main Street',
        suburb: 'Sydney',
        state: 'NSW',
        postcode: '2000',
        country: 'Australia',
        nok_name: 'Jane Smith',
        nok_relation: 'Spouse',
        nok_phone: '+1 (555) 987-6543',
        nok_email: 'jane.smith@email.com',
        medicare: '1234567890A'
      };

      const mockAccountsDetails = {
        medicare_number: '1234567890A',
        medicare_expiry_date: '2025-12-31',
        pensioncard_number: 'PC123456789',
        pensioncard_expiry_date: '2024-06-30'
      };
      
      console.log('Mock patient data loaded:', mockPatientData);
      setPatient(mockPatientData);
      setEditablePatient({ ...mockPatientData });
      setPatientAccountsDetails(mockAccountsDetails);
      setEditableAccountsDetails({ ...mockAccountsDetails });
      setLoading(false);
    };

    // Start by trying to load real data
    loadPatientData();
  }, [patientId]);

  // Helper functions
  const calculateAge = (dob) => {
    if (!dob) return 'Unknown';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age.toString();
  };

  const handleInputChange = (field, value, section = 'patient') => {
    if (section === 'accounts') {
      setEditableAccountsDetails(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setEditablePatient(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleEditRecord = (section) => {
    setEditingSection(section);
  };

  const handleCancelChanges = (section) => {
    if (section === 'other') {
      setEditableAccountsDetails({ ...patientAccountsDetails });
    } else {
      setEditablePatient({ ...patient });
    }
    setEditingSection(null);
  };

  const handleSaveChanges = (section) => {
    if (section === 'other') {
      setPatientAccountsDetails({ ...editableAccountsDetails });
    } else {
      setPatient({ ...editablePatient });
    }
    setEditingSection(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-gray-500">
            Loading patient details...
          </div>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-gray-500">
            {error || 'Patient not found'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Details Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <h4 className="font-semibold">General Details</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsGeneralDetailsExpanded(!isGeneralDetailsExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isGeneralDetailsExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isGeneralDetailsExpanded && (
        <div className="p-4">
          {/* Single row layout for all input fields */}
          <div className="flex gap-3">
            {/* Patient ID - smaller width */}
            <div className="flex-shrink-0 w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
              <input
                type="text"
                value={editingSection === 'general' ? editablePatient.patient_id : patient.patient_id}
                onChange={(e) => handleInputChange('patient_id', e.target.value)}
                readOnly={editingSection !== 'general'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Patient Name - increased width by 20% */}
            <div className="flex-shrink-0 w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
              <input
                type="text"
                value={editingSection === 'general' ? `${editablePatient.first_name} ${editablePatient.last_name}` : `${patient.first_name} ${patient.last_name}`}
                onChange={(e) => {
                  const [firstName, ...lastNameParts] = e.target.value.split(' ');
                  handleInputChange('first_name', firstName || '');
                  handleInputChange('last_name', lastNameParts.join(' ') || '');
                }}
                readOnly={editingSection !== 'general'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Date of Birth - increased width */}
            <div className="flex-shrink-0 w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Of Birth:</label>
              <input
                type="date"
                value={patient.dob}
                readOnly
                className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900"
              />
            </div>

            {/* Age - increased width */}
            <div className="flex-shrink-0 w-28">
              <label className="block text-sm font-medium text-gray-700 mb-1">Age:</label>
              <input
                type="text"
                value={calculateAge(patient.dob)}
                readOnly
                className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900"
              />
            </div>

            {/* Gender - increased width */}
            <div className="flex-shrink-0 w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender:</label>
              <input
                type="text"
                value={patient.gender || "Not specified"}
                readOnly
                className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'general' ? (
              <button 
                onClick={() => handleEditRecord('general')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('general')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('general')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Contact Details Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Phone className="h-5 w-5" />
              <h4 className="font-semibold">Contact Details</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsContactDetailsExpanded(!isContactDetailsExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isContactDetailsExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isContactDetailsExpanded && (
        <div className="p-4">
          {/* Two-row layout for Contact Details */}
          <div className="space-y-3">
            {/* First Row: Phone and Email */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.phone : patient.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.user_email : (patient.user_email || patient.email || 'Not provided')}
                  onChange={(e) => handleInputChange('user_email', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
            </div>

            {/* Second Row: Address, Suburb, State, Postcode, Country */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.address : patient.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
              <div className="flex-shrink-0 w-32">
                <label className="block text-sm font-medium text-gray-700 mb-1">Suburb:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.suburb : patient.suburb}
                  onChange={(e) => handleInputChange('suburb', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
              <div className="flex-shrink-0 w-24">
                <label className="block text-sm font-medium text-gray-700 mb-1">State:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.state : patient.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
              <div className="flex-shrink-0 w-28">
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.postcode : patient.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
              <div className="flex-shrink-0 w-32">
                <label className="block text-sm font-medium text-gray-700 mb-1">Country:</label>
                <input
                  type="text"
                  value={editingSection === 'contact' ? editablePatient.country : patient.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  readOnly={editingSection !== 'contact'}
                  className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                    editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'contact' ? (
              <button 
                onClick={() => handleEditRecord('contact')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('contact')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('contact')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Next Of Kin Details Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <UserCheck className="h-5 w-5" />
              <h4 className="font-semibold">Next Of Kin Details</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsNextOfKinDetailsExpanded(!isNextOfKinDetailsExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isNextOfKinDetailsExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isNextOfKinDetailsExpanded && (
        <div className="p-4">
          {/* Single row layout for all Next of Kin fields */}
          <div className="flex gap-3">
            {/* Next of Kin Name */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Next Of Kin Name:</label>
              <input
                type="text"
                value={editingSection === 'nextOfKin' ? editablePatient.nok_name : (patient.nok_name || 'Not provided')}
                onChange={(e) => handleInputChange('nok_name', e.target.value)}
                readOnly={editingSection !== 'nextOfKin'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Next of Kin Relationship */}
            <div className="flex-shrink-0 w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Relationship:</label>
              <input
                type="text"
                value={editingSection === 'nextOfKin' ? editablePatient.nok_relation : (patient.nok_relation || 'Not specified')}
                onChange={(e) => handleInputChange('nok_relation', e.target.value)}
                readOnly={editingSection !== 'nextOfKin'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Next of Kin Phone */}
            <div className="flex-shrink-0 w-44">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone:</label>
              <input
                type="text"
                value={editingSection === 'nextOfKin' ? editablePatient.nok_phone : (patient.nok_phone || 'Not provided')}
                onChange={(e) => handleInputChange('nok_phone', e.target.value)}
                readOnly={editingSection !== 'nextOfKin'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Next of Kin Email */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
              <input
                type="text"
                value={editingSection === 'nextOfKin' ? editablePatient.nok_email : (patient.nok_email || 'Not provided')}
                onChange={(e) => handleInputChange('nok_email', e.target.value)}
                readOnly={editingSection !== 'nextOfKin'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
          </div>
          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'nextOfKin' ? (
              <button 
                onClick={() => handleEditRecord('nextOfKin')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('nextOfKin')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('nextOfKin')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Other Details Panel */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5" />
              <h4 className="font-semibold">Other Details</h4>
            </div>
            <button
              type="button"
              onClick={() => setIsOtherDetailsExpanded(!isOtherDetailsExpanded)}
              className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
            >
              {isOtherDetailsExpanded ? (
                <Minus className="h-3 w-3" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
            </button>
          </div>
        </div>
        {isOtherDetailsExpanded && (
        <div className="p-4">
          {/* Single row layout for all Other Details fields */}
          <div className="flex gap-3">
            {/* Medicare Number */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Number:</label>
              <input
                type="text"
                value={editingSection === 'other' ? editableAccountsDetails.medicare_number : (patientAccountsDetails?.medicare_number || patient.medicare || 'Not provided')}
                onChange={(e) => handleInputChange('medicare_number', e.target.value, 'accounts')}
                readOnly={editingSection !== 'other'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Medicare Expiry Date */}
            <div className="flex-shrink-0 w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Expiry:</label>
              <input
                type="date"
                value={editingSection === 'other' ? editableAccountsDetails.medicare_expiry_date : (patientAccountsDetails?.medicare_expiry_date || patient.medicare_expiry || '')}
                onChange={(e) => handleInputChange('medicare_expiry_date', e.target.value, 'accounts')}
                readOnly={editingSection !== 'other'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Pension Card Number */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pension Card Number:</label>
              <input
                type="text"
                value={editingSection === 'other' ? editableAccountsDetails.pensioncard_number : (patientAccountsDetails?.pensioncard_number || patient.pension_card || 'NA')}
                onChange={(e) => handleInputChange('pensioncard_number', e.target.value, 'accounts')}
                readOnly={editingSection !== 'other'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>

            {/* Pension Card Expiry Date */}
            <div className="flex-shrink-0 w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pension Expiry:</label>
              <input
                type="text"
                value={editingSection === 'other' ? editableAccountsDetails.pensioncard_expiry_date : ((patientAccountsDetails?.pensioncard_number || patient.pension_card) ? 
                  (patientAccountsDetails?.pensioncard_expiry_date || patient.pension_expiry || 'NA') : 
                  'NA')}
                onChange={(e) => handleInputChange('pensioncard_expiry_date', e.target.value, 'accounts')}
                readOnly={editingSection !== 'other'}
                className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                  editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                }`}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4 space-x-2">
            {editingSection !== 'other' ? (
              <button 
                onClick={() => handleEditRecord('other')}
                className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Record</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleCancelChanges('other')}
                  className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={() => handleSaveChanges('other')}
                  className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                >
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default GeneralDetailsContent;